export const ADMIN = "admin";
export const BROKER = "broker";
export const AGENT = "agent";
export const ANALYST = "analyst";
export const CONSULTANT = "consultant";
// Broker-side analyst user — scoped to a single broker like AGENT, but
// granted analyst capabilities on /rfps/*. See ADR 10.19.
export const BROKER_ANALYST = "broker_analyst";

export const RFPS_DRAFT = 1;
export const RFPS_SUBMITTED = 0;
export const RFPS_TAB_MAPPING: Record<number, string> = {
  0: "submitted",
  1: "draft",
};

export const OWNER = "owner";
export const MEMBER = "member";

export const PENDING = "Pending";
export const ACCEPTED = "Accepted";
export const EXPIRED = "Expired";
export const CANCELLED = "Cancelled";

export const ONE_DIGITAL = "OneDigital";
export const CENTRO = "Centro";

export { OPEN_PAGES, PREV_URL, ROUTE_NAMES } from "@platform/types";

export const MAX_MB_RFP_FILE_SIZE = 40;
/* SharePoint enforces a limit on the total URL length, which includes:
 - The full folder path where the file is stored
 - The encoded file name (with special characters like spaces converted to %20)
 - Any additional query parameters or operations performed in the request

 To avoid errors when uploading files, we restrict the *encoded* file name length
 so that when combined with the full SharePoint path, it stays within the supported limit.

 This MAX_ALLOWED_CHARACTER_FILE_NAME_LENGTH value has been tested and found to be
 safe across our environments for successful uploads to SharePoint.
*/
export const MAX_ALLOWED_CHARACTER_FILE_NAME_LENGTH = 200;

export const MAX_MB_RFP_PROPOSAL_FILE_SIZE = 100;

export const ACCEPTED_FILE_FORMATS: Record<string, string[]> = {
  "application/customTypes": [
    ".pdf",
    ".xls",
    ".xlsx",
    ".xlsb",
    ".xlsm",
    ".csv",
    ".doc",
    ".docx",
  ],
};
export const CARRIER_PROPOSAL_ACCEPTED_FILE_FORMATS: Record<string, string[]> =
  {
    "application/customTypes": [
      ".pdf",
      ".xls",
      ".xlsx",
      ".xlsb",
      ".xlsm",
      ".csv",
      ".doc",
      ".docx",
      ".zip",
    ],
  };
export const MARKETING_ACCEPTED_FILE_FORMATS: Record<string, string[]> = {
  "application/customTypes": [
    ".pdf",
    ".xls",
    ".xlsx",
    ".xlsb",
    ".xlsm",
    ".csv",
    ".doc",
    ".docx",
    ".zip",
  ],
};

export const ACCEPTED_FILE_FORMATS_LABEL = "PDF, XLS, CSV, or DOC";
export const CARRIER_PROPOSAL_ACCEPTED_FILE_FORMATS_LABEL =
  "ZIP, PDF, XLS, CSV, or DOC";
export const MARKETING_ACCEPTED_FILE_FORMATS_LABEL =
  "ZIP, PDF, XLS, CSV, or DOC";

export const ACCEPTED_FILE_MINE_TYPES: string[] = [
  "application/pdf",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/zip",
  "application/x-zip-compressed",
  "application/vnd.ms-excel.sheet.macroenabled.12",
  "application/vnd.ms-excel.sheet.macroEnabled.12",
  "application/vnd.ms-excel.sheet.binary.macroEnabled.12",
  "application/vnd.ms-excel.sheet.binary.macroenabled.12",
  "application/vnd.ms-excel.addin.macroEnabled.12",
];

export const COMPLETE_ERROR_CODES: string[] = [
  "additional-exist-in-other-documentType",
  "other-documentType-duplicate",
  "current-documentType-duplicate",
  "too-many-files",
  "file-name-over-limit",
];

export const RFP_FILES_TYPES: Record<string, string> = {
  PROPOSAL: "proposal",
  DOCUMENT: "document",
};

export const STATE_RULES: Record<string, string> = {
  NORMAL: "normal",
  DONE: "done",
  WRONG: "wrong",
};

export const initPasswordRulesConfirm: { label: string; state: string }[] = [
  { label: "Passwords must match", state: STATE_RULES.NORMAL },
];

const regexLowerCase = /[a-z]/;
const regexUpperCase = /[A-Z]/;
const regexNumber = /[0-9]/;

type PasswordRule = {
  label: string;
  state: string;
  validation: (password: string) => boolean;
};

export const initPasswordRules: PasswordRule[] = [
  {
    label: "Must contain at least one lowercase letter",
    state: STATE_RULES.NORMAL,
    validation: (password) => regexLowerCase.test(password),
  },
  {
    label: "Must contain at least one uppercase letter",
    state: STATE_RULES.NORMAL,
    validation: (password) => regexUpperCase.test(password),
  },
  {
    label: "Must contain one number",
    state: STATE_RULES.NORMAL,
    validation: (password) => regexNumber.test(password),
  },
  {
    label: "Must be at least 8 characters",
    state: STATE_RULES.NORMAL,
    validation: (password) => password.length >= 8,
  },
];

export const INVITE_FORM: Record<string, string> = {
  FIRST_NAME: "firstName",
  LAST_NAME: "lastName",
  EMAIL: "email",
  CONFIRMATION_EMAIL: "confirmationEmail",
  ROLE: "role",
};
export const STATUS_CODE: Record<string, number> = {
  UNAUTHORIZED: 401,
};

// we can check for additional codes: https://learn.microsoft.com/en-us/azure/active-directory-b2c/error-codes
export const WRONG_CREDENTIALS_CODE_B2C = "AADB2C90225";
export const WRONG_CREDENTIALS_TEXT_B2C =
  "The email and/or password you entered did not match our records. Please try again.";

export const PENDING_CARRIER = "CARRIER_TBD";

export const FUNDING_TYPE: Record<string, string> = {
  fullyInsured: "Fully Insured",
  selfFundedASO: "Self-Funded ASO",
  selfFundedATP: "Self-Funded ATP",
};

export const USER_STORAGE_KEY = "user";

export const TOKEN_USER_ROLES = "centro_bp/roles";

export const STATE_ABBREVIATION: Record<string, string> = {
  Alabama: "AL",
  Kentucky: "KY",
  Ohio: "OH",
  Alaska: "AK",
  Louisiana: "LA",
  Oklahoma: "OK",
  Arizona: "AZ",
  Maine: "ME",
  Oregon: "OR",
  Arkansas: "AR",
  Maryland: "MD",
  Pennsylvania: "PA",
  Massachusetts: "MA",
  "Puerto Rico": "PR",
  California: "CA",
  Michigan: "MI",
  "Rhode Island": "RI",
  Colorado: "CO",
  Minnesota: "MN",
  "South Carolina": "SC",
  Connecticut: "CT",
  Mississippi: "MS",
  "South Dakota": "SD",
  Delaware: "DE",
  Missouri: "MO",
  Tennessee: "TN",
  "District of Columbia": "DC",
  Montana: "MT",
  Texas: "TX",
  Florida: "FL",
  Nebraska: "NE",
  Georgia: "GA",
  Nevada: "NV",
  Utah: "UT",
  "New Hampshire": "NH",
  Vermont: "VT",
  Hawaii: "HI",
  "New Jersey": "NJ",
  Virginia: "VA",
  Idaho: "ID",
  "New Mexico": "NM",
  "US Virgin Islands": "VI",
  Illinois: "IL",
  "New York": "NY",
  Washington: "WA",
  Indiana: "IN",
  "North Carolina": "NC",
  "West Virginia": "WV",
  Iowa: "IA",
  "North Dakota": "ND",
  Wisconsin: "WI",
  Kansas: "KS",
  Wyoming: "WY",
};

export const AUTO_HIDE_DURATION_MS = 2000;

export const PAGINATION_PAGE_SIZE_OPTIONS: number[] = [10, 25, 50];
